import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@campgladiator/cgui-core.atoms.button'
import { Heading } from '@campgladiator/cgui-core.atoms.typography'
import {
  ControlsProps,
  OverflowContainerProps,
  ScrollBtnProps,
} from '../../TrainerBio.d'
import styles from './OverflowContainer.module.scss'

const ScrollBtn = ({ direction, onClick, ...props }: ScrollBtnProps) => (
  <Button
    emphasis="secondary-alt"
    variation="outline"
    size="small"
    icon={
      direction === 'left' ? 'icon-angle-left-line' : 'icon-angle-right-line'
    }
    onClick={onClick}
    {...props}
  />
)

const Controls = ({
  className,
  scrollElementRef,
  handleOnClick = () => {},
  ...props
}: ControlsProps) => {
  const [shouldRender, setShouldRender] = useState(false)
  const [, setScrollPosition] = useState(0)

  useEffect(() => {
    scrollElementRef.current && setShouldRender(true)
  }, [scrollElementRef])

  useEffect(() => {
    const current = scrollElementRef.current!
    current.addEventListener('scroll', () =>
      setScrollPosition(current.scrollLeft),
    )
    return () => {
      current.addEventListener('scroll', () =>
        setScrollPosition(current.scrollLeft),
      )
    }
  }, [scrollElementRef])

  const shouldDisableScrollLeft =
    !!scrollElementRef.current && scrollElementRef.current.scrollLeft === 0

  const shouldDisableScrollRight =
    !!scrollElementRef.current &&
    scrollElementRef.current.scrollLeft >=
      scrollElementRef.current.scrollWidth -
        scrollElementRef.current.offsetWidth

  const determineChildrenPositions = () => {
    const childrenElems = scrollElementRef.current?.childNodes
    const leftPadding = parseFloat(
      window.getComputedStyle(scrollElementRef.current!).paddingLeft,
    )
    const childrenElemsPositions: number[] = []
    childrenElems?.forEach((element) => {
      childrenElemsPositions.push(
        (element as HTMLElement).offsetLeft -
          scrollElementRef.current!.offsetLeft -
          leftPadding,
      )
    })
    return childrenElemsPositions
  }

  const scrollRight = () => {
    handleOnClick()
    const childrenPositions = determineChildrenPositions()
    const newScrollPosition = childrenPositions.find(
      (elem) =>
        Math.round(elem) >
        Math.round(scrollElementRef.current?.scrollLeft || 0),
    )
    if (newScrollPosition !== undefined)
      scrollElementRef.current!.scrollLeft = newScrollPosition
  }

  const scrollLeft = () => {
    handleOnClick()
    const childrenPositions = determineChildrenPositions()
    const newScrollPosition = childrenPositions
      .reverse()
      .find(
        (elem) =>
          Math.round(elem) <
          Math.round(scrollElementRef.current?.scrollLeft || 0),
      )
    if (newScrollPosition !== undefined)
      scrollElementRef.current!.scrollLeft = newScrollPosition
  }

  return shouldRender ? (
    <div className={styles.scroll} {...props}>
      <ScrollBtn
        direction="left"
        onClick={scrollLeft}
        disabled={shouldDisableScrollLeft}
      />
      <ScrollBtn
        direction="right"
        onClick={scrollRight}
        disabled={shouldDisableScrollRight}
      />
    </div>
  ) : null
}

const OverflowContainer = ({
  className,
  heading,
  children,
  scrollOnClick = () => {},
  ...props
}: OverflowContainerProps) => {
  const divRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <header className={styles.header}>
        <Heading type="h5">{heading}</Heading>
        <Controls scrollElementRef={divRef} handleOnClick={scrollOnClick} />
      </header>
      <div
        ref={divRef}
        className={classNames(styles.wrapper, className)}
        {...props}
      >
        {children}
      </div>
    </>
  )
}

export default OverflowContainer
